import {ButtonSubmit, InputFieldWithFloatingLabel} from '@wr/ui-patterns';
import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {CREDIT_CARD_PAYMENT_METHODS} from '../../../consts';
import Spinner from '../../atoms/Spinner';
import {pushAnalyticsEvent} from '../../organisms/Analytics';

const CVV_FIELD_NAME = 'cvv';
const CANCEL_BUTTON_NAME = 'cancel-link';
const cancelUrlPath = 'transaction/payment-selection?status=failure';

const CreditCardForm = ({
    disableSubmitButton, translations, onSubmit, languageCode, showCancelButton, paymentMethod
}) => {
    const [cvvValue, setCvvValue] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCvvValueDirty, setIsCvvValueDirty] = useState(false);

    const formName = `new-${paymentMethod}-payment-form-saved-card`;

    let cvvErrorText = '';
    if (isCvvValueDirty) {
        if (cvvValue.length === 0) {
            cvvErrorText = translations[paymentMethod].errors.securityCode.required;
        } else if (!/^(\d{3})$/.test(cvvValue)) {
            cvvErrorText = translations[paymentMethod].errors.securityCode[
                paymentMethod === CREDIT_CARD_PAYMENT_METHODS.ZEPZ ? 'format' : 'invalid'
            ];
        }
    }

    const isSubmitButtonDisabled =
        cvvValue === '' ||
        !!cvvErrorText ||
        isSubmitting ||
        disableSubmitButton;

    const handleCvvChange = value => {
        setCvvValue(value);

        if (!isCvvValueDirty) {
            setIsCvvValueDirty(true);
        }
    };

    const handleCvvBlur = event => {
        if (!isCvvValueDirty && event?.relatedTarget?.className !== CANCEL_BUTTON_NAME) {
            setIsCvvValueDirty(true);
        }

        pushAnalyticsEvent({
            interactionName: 'form tracking',
            formName,
            formField: CVV_FIELD_NAME
        });
    };

    const handleSubmit = event => {
        event.preventDefault();

        if (disableSubmitButton) {
            return;
        }

        if (cvvErrorText) {
            pushAnalyticsEvent({
                interactionName: 'form error',
                formName,
                formField: CVV_FIELD_NAME,
                formMessage: cvvErrorText
            });
            pushAnalyticsEvent({
                interactionName: 'form submit',
                formName,
                formResult: 'failure'
            });
            return;
        }

        setIsSubmitting(true);
        pushAnalyticsEvent({
            interactionName: 'form submit',
            formName,
            formResult: 'unknown'
        });
        onSubmit(cvvValue);
    };

    const handleCancelClick = event => {
        event.preventDefault();

        pushAnalyticsEvent({
            interactionName: 'form cancel',
            formName,
            eventCallback: () => {
                window.location.assign(`/${languageCode}/${cancelUrlPath}`);
            },
            eventTimeout: 500
        });
    };

    return (
        <form action="" onSubmit={handleSubmit}>
            <div className="popup-form">
                <InputFieldWithFloatingLabel
                    placeholder="CVV"
                    id="cvv-creditcard"
                    className="cvv-creditcard"
                    name={CVV_FIELD_NAME}
                    label="CVV"
                    type="number"
                    required
                    value={cvvValue}
                    onChange={handleCvvChange}
                    onBlur={handleCvvBlur}
                    maxLength="3"
                    autoComplete="off"
                />

                {cvvErrorText && <p className="error-cvv">{cvvErrorText}</p>}

                <div className="popup-submit-wrapper">
                    {isSubmitting && <Spinner />}
                    <ButtonSubmit
                        className="popup-submit-btn"
                        type="mega-cta"
                        disabled={isSubmitButtonDisabled}
                        text={translations.common.continue}
                    />
                    {showCancelButton ? (
                        <a
                            className={CANCEL_BUTTON_NAME}
                            href={`/${languageCode}/${cancelUrlPath}`}
                            onClick={handleCancelClick}
                        >
                            {translations.common.cancel}
                        </a>
                    ) : null }
                    <p className="warning-text">{translations.creditCard.popUp.warningText}</p>
                </div>
            </div>
        </form>
    );
};

CreditCardForm.propTypes = {
    disableSubmitButton: PropTypes.bool.isRequired,
    translations: PropTypes.shape({
        common: PropTypes.shape({
            continue: PropTypes.string.isRequired,
            cancel: PropTypes.string.isRequired
        }).isRequired,
        creditCard: PropTypes.shape({
            popUp: PropTypes.shape({
                warningText: PropTypes.string.isRequired
            })
        }),
        cybersource: PropTypes.shape({
            errors: PropTypes.shape({
                securityCode: PropTypes.shape({
                    required: PropTypes.string.isRequired,
                    invalid: PropTypes.string.isRequired
                })
            })
        }),
        zepz: PropTypes.shape({
            errors: PropTypes.shape({
                securityCode: PropTypes.shape({
                    required: PropTypes.string.isRequired,
                    format: PropTypes.string.isRequired
                })
            })
        })
    }).isRequired,
    onSubmit: PropTypes.func.isRequired,
    languageCode: PropTypes.string.isRequired,
    showCancelButton: PropTypes.bool.isRequired,
    paymentMethod: PropTypes.oneOf([
        CREDIT_CARD_PAYMENT_METHODS.CYBERSOURCE,
        CREDIT_CARD_PAYMENT_METHODS.ZEPZ
    ]).isRequired
};

export default CreditCardForm;
